// MessageBox.tsx
import React, { useState, useEffect, useRef, lazy, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Typography,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar as MuiAvatar,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  InfoRounded as InfoRoundedIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Audiotrack as AudiotrackIcon,
  OndemandVideo as OndemandVideoIcon,
  SupportAgent as SupportAgentIcon,
  Send as SendIcon,
  People as PeopleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import Avatar from './avatar.jpg';
import {
  alertAtom,
  interveneFilterAtom,
  // isMobileDeviceAtom,
  messageListAtom,
  showMobileViewAtom,
  userDetailAtom,
  userListAtom,
  WhatsAppTempFormAtom,
  chatCountsAtom,
} from '../../Services/_states/index.js';
import { InterveneActions } from '../../Services/_actions/Intervene/intervene.actions.js';
import { dateFormate, indianTimeZone } from '../../utils/helper.jsx';
import FileUploadPreview from '../Shared/FileUploadPreview/index.jsx';
import MessageNode from '../LeadWebForm/MessageNode/MessageType/MessageNode.tsx';
import DataNotFound from '../Shared/DataNotFound/index.jsx';
import { CallingModal } from '../Modals/CallingModal/CallingModal.jsx';
import SendTemplateModal from '../Modals/SendTemplateModal/index.jsx';
import LoadingWrapper from '../Shared/LoadingWrapper/index.jsx';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CommonBadges from '../Shared/Badges';
import MessageDesign from './MessageDesign.tsx';
import { userInformationAtom } from '../../Services/_states/index.js';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useMessageStatusSocket } from './useMessageStatusSocket.ts';

import { BLOCKED_EXTENSIONS, WHATSAPP_ALLOWED_MIME, WHATSAPP_LIMITS } from '../Whatsapp/Template/template.constant.js';
import LoadingButton from '../Shared/Loading/index.tsx';
const ModalForms = lazy(() => import('../Shared/Modals/index.js'));
const VoiceRecorder = lazy(() => import('./VoiceRecorder/index.jsx'));

const MessageBox: React.FC<MessageBoxProps> = ({ currentUsersOnChat = [] }) => {
  const navigate = useNavigate();
  const actions = InterveneActions();
  const { showSnackBar } = useSnackBar();
  const setUserList = useRecoilValue(userListAtom);
  const [interveneFilters, setInterveneFilters] = useRecoilState(interveneFilterAtom);
  const [userDetails, setUserDetails] = useRecoilState<any>(userDetailAtom);
  const [messageList, setMessageList] = useRecoilState<any>(messageListAtom);
  const [showMobileView, setShowMobileView] = useRecoilState(showMobileViewAtom);
  // const isMobile = useRecoilValue(isMobileDeviceAtom);
  const isMobile = !useMediaQuery('(max-width:1024px)');
  const [isLoading, setIsLoading] = useRecoilState(alertAtom);
  const [chatCounts, setChatCounts] = useRecoilState(chatCountsAtom);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const loggedInUser = useRecoilValue(userInformationAtom);

  const messageBoxRef = useRef<HTMLDivElement>(null);
  const uploadRefs = useRef<any>({ image: null, doc: null, audio: null, video: null });

  const [pageNumber, setPageNumber] = useState(1);
  const [sendMessage, setSendMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [docType, setDocType] = useState('');
  const [modalShowTemp, setModalShowTemp] = useState(false);
  const [modalShowCall, setModalShowCall] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [formAnchorEl, setFormAnchorEl] = useState<null | HTMLElement>(null);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [lastSeenDate, setLastSeenDate] = useState('');
  const [hasFetchedMessages, setHasFetchedMessages] = useState(false);
  const open = Boolean(anchorEl);
  const formMenuOpen = Boolean(formAnchorEl);
  const checkSession = userDetails?.intervene && userDetails?.session_start;
  const resetWhatsappTempFormData = useResetRecoilState(WhatsAppTempFormAtom);
  const canEndIntervene = checkSession && userDetails?.intervened_by === loggedInUser?.username;
  const prevScrollHeight = useRef(0);
  const statusSocketUrl =
    userDetails?.bot_number && userDetails?.phone_number
      ? `${process.env.REACT_APP_WEBSOCKET_URL}/message-status/${userDetails.bot_number}/${userDetails.phone_number}/`
      : '';
  useMessageStatusSocket({ url: statusSocketUrl });
  const draftsRef = useRef<Record<string, string>>({});
  const prevPhoneRef = useRef<string>('');
  const [isInterveneLoading, setIsInterveneLoading] = useState(false);

  // const WHATSAPP_LIMITS = {
  //   image: 5 * 1024 * 1024,   // 5 MB
  //   video: 16 * 1024 * 1024,  // 16 MB
  //   audio: 16 * 1024 * 1024,  // 16 MB
  //   file: 100 * 1024 * 1024,  // 100 MB (documents)
  // } as const;

  // const ALLOWED_MIME: Record<string, string[]> = {
  //   image: ['image/jpeg', 'image/png'],
  //   video: ['video/mp4', 'video/3gpp'],
  //   audio: ['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg'],
  //   file: [
  //     'application/pdf',
  //     'application/msword',
  //     'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  //     'application/vnd.ms-excel',
  //     'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  //     'application/vnd.ms-powerpoint',
  //     'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  //     'text/plain',
  //     'text/csv',
  //   ],
  // };

  // const BLOCKED_EXTENSIONS = new Set([
  //   '.exe', '.msi', '.cmd', '.bat', '.com', '.scr', '.dll',
  //   '.sh', '.bash', '.ksh', '.zsh', '.ps1', '.psm1',
  //   '.vbs', '.js', '.mjs', '.ts', '.tsx', '.jsx',
  //   '.py', '.pyc', '.pyo', '.rb', '.php', '.pl', '.jar',
  //   '.apk', '.ipa', '.reg', '.sql',
  // ]);

  interface MessageBoxProps {
    currentUsersOnChat?: string[];
  }


  const getExt = (name: string) => {
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.slice(i).toLowerCase() : '';
  };

  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);


  // Infer our logical "docType" bucket from MIME
  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };

  const formatDateLabel = (dateStr: string) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const msgDate = new Date(dateStr);

    const isToday =
      msgDate.getDate() === today.getDate() &&
      msgDate.getMonth() === today.getMonth() &&
      msgDate.getFullYear() === today.getFullYear();

    const isYesterday =
      msgDate.getDate() === yesterday.getDate() &&
      msgDate.getMonth() === yesterday.getMonth() &&
      msgDate.getFullYear() === yesterday.getFullYear();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return msgDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 1) Block disallowed / risky extensions
    const ext = getExt(file.name);
    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
      e.currentTarget.value = ''; // reset input
      return;
    }

    // 2) Validate MIME (must match our allowed lists)
    const typeGroup = inferDocType(file.type);
    if (!typeGroup) {
      showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
      e.currentTarget.value = '';
      return;
    }

    // 3) Enforce WhatsApp limits per media group
    const limit = WHATSAPP_LIMITS[typeGroup];
    if (file.size > limit) {
      showSnackBar(
        `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
        'error'
      );
      e.currentTarget.value = '';
      return;
    }

    setDocType(typeGroup);
    setSelectedFiles([file]);
    setAnchorEl(null);
  };


  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;

  //   const type = file.type;
  //   if (
  //     ['image/jpeg', 'image/png', 'image/webm', 'image/webp', 'image/avif', 'image/WEBM'].includes(
  //       type
  //     )
  //   )
  //     setDocType('image');
  //   else if (
  //     [
  //       'application/pdf',
  //       'text/plain',
  //       'application/msword',
  //       'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  //     ].includes(type)
  //   )
  //     setDocType('file');
  //   else if (['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a', 'audio/aac'].includes(type))
  //     setDocType('audio');
  //   else if (['video/mp4', 'video/quicktime'].includes(type)) setDocType('video');
  //   else return showSnackBar('Unsupported file type', 'error');

  //   setSelectedFiles([file]);
  //   setAnchorEl(null);
  // };

  const deleteFile = () => {
    setSelectedFiles([]);
    setDocType('');
  };

  const getMessagesByNumber = async (filter: any) => {
    if (filter?.phone_number) {
      setIsLoading(true);
      if ((filter?.page ?? 1) === 1) {
        setHasFetchedMessages(false);
      }
      try {
        await actions.getUserMessages(filter);
      } finally {
        setHasFetchedMessages(true);
      }
    }
  };

  const sendMessageByNumber = async () => {
    // avoid double-submits
    if (isMessageSending) return;

    if (!userDetails?.phone_number) {
      showSnackBar('Missing number', 'error');
      return;
    }
    if (checkLastMessageDateTime() || !checkSession) return;

    // const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

    setIsMessageSending(true);
    try {
      const file = selectedFiles[0];
      if (file) {
        const ext = getExt(file.name);
        if (BLOCKED_EXTENSIONS.has(ext)) {
          showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
          return;
        }

        const inferredType = inferDocType(file.type);
        if (!inferredType) {
          showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
          return;
        }

        const bucket = inferredType;

        const limit = WHATSAPP_LIMITS[bucket];
        if (file.size > limit) {
          showSnackBar(
            `File too large for ${bucket}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
            'error'
          );
          return;
        }
      } else {
        if (!sendMessage?.trim()) {
          showSnackBar('Please type a message or attach a file.', 'warning');
          return;
        }
      }
      const form = new FormData();
      form.append('phone_number', userDetails.phone_number);

      if (file) {
        form.append('file', file);
        const safeDocType = inferDocType(file.type) ?? (docType as any);
        const docTypes = ['.doc', '.docx', '.pdf', '.txt'];
        if (safeDocType === 'image') {
          form.append('text', sendMessage);
          form.append('type', 'image');
        } else if (safeDocType === 'video') {
          form.append('text', sendMessage);
          form.append('type', 'video');
        }
        else if (safeDocType === 'file') {
          form.append('text', sendMessage);
          form.append('type', 'document');
        }
        else {
          form.append('type', safeDocType);
        }
      } else {
        form.append('text', sendMessage.trim());
        form.append('type', 'text');
      }

      await actions.sendMessageV1(form);
      setSendMessage('');
      delete draftsRef.current[userDetails.phone_number];

      setSelectedFiles([]);
      setDocType('');
      showSnackBar('Message sent.', 'success');
      setMessageList((prev) => ({
        ...prev,
        filters: { ...prev.filters, page: 1 },
      }));
    } catch (err: any) {
      console.error(err);
      showSnackBar('Failed to send message.', 'error');
    } finally {
      setIsMessageSending(false);
    }
  };


  // const sendMessageByNumber = async () => {
  //   if (!userDetails?.phone_number) return showSnackBar('Missing number', 'error');
  //   if (checkLastMessageDateTime() || !checkSession) return; // Prevent sending if not allowed

  //   setIsMessageSending(true);

  //   const form = new FormData();
  //   form.append('phone_number', userDetails.phone_number);
  //   if (selectedFiles[0]) {
  //     form.append('file', selectedFiles[0]);
  //     if (docType === 'image') {
  //       form.append('caption', sendMessage);
  //       form.append('type', 'image');
  //     } else {
  //       form.append('type', docType);
  //     }
  //   } else {
  //     form.append('text', sendMessage);
  //     form.append('type', 'text');
  //   }

  //   await actions.sendMessageV1(form);
  //   setSendMessage('');
  //   setSelectedFiles([]);
  //   setDocType('');
  //   setIsMessageSending(false);
  // };

  // const scrollToBottom = () => {
  //   const box = messageBoxRef.current;
  //   if (!box) return;
  //   if (messageList?.filters?.page === 1) {
  //     box.scrollTop = box.scrollHeight;
  //     const reversedArray = [...messageList?.messages].reverse();
  //     let check = true;
  //     reversedArray.reverse().forEach((element: any) => {
  //       if (element?.origin === 'INBOUND' && check) {
  //         setLastSeenDate(element?.created_at);
  //         check = false;
  //       }
  //     });
  //   } else {
  //     box.scrollTop = box.scrollHeight / 3;
  //   }
  // };

  // const handleScroll = () => {
  //   const box = messageBoxRef.current;
  //   if (!box || messageList.messages.length >= messageList.total_count) return;
  //   if (box.scrollTop === 0) {
  //     setMessageList((prev) => ({ ...prev, filters: { ...prev.filters, page: prev?.filters?.page + 1 } }));
  //     // setPageNumber((prev) => prev + 1);
  //     // getMessagesByNumber({ ...userDetails, page: pageNumber + 1 });
  //   }
  // };

  const handleScroll = () => {
    const box = messageBoxRef.current;
    if (!box || messageList.messages.length >= messageList.total_count) return;

    if (box.scrollTop === 0) {
      prevScrollHeight.current = box.scrollHeight;

      setMessageList((prev) => ({
        ...prev,
        filters: { ...prev.filters, page: prev?.filters?.page + 1 }
      }));
    }
  };

  useEffect(() => {
    const box = messageBoxRef.current;
    if (!box) return;

    if (messageList?.filters?.page > 1) {
      const newHeight = box.scrollHeight;
      box.scrollTop = newHeight - prevScrollHeight.current;
    } else {
      box.scrollTop = box.scrollHeight;
    }
  }, [messageList]);

  const getTabAfterEndIntervene = (lastActiveDate: string): '15-min' | 'active' => {
    if (!lastActiveDate) return 'active';

    const givenDate = new Date(indianTimeZone(lastActiveDate));
    const currentDate = new Date();
    const diffInMs = currentDate.getTime() - givenDate.getTime();
    const fifteenMinutesInMs = 15 * 60 * 1000;

    return diffInMs <= fifteenMinutesInMs ? '15-min' : 'active';
  };

  const handleClickStartSession = async () => {
    if (isInterveneLoading) return;

    setIsInterveneLoading(true);

    try {
      const isSessionOngoing = userDetails?.intervene && userDetails?.session_start;
      const currentPhone = userDetails?.phone_number;

      const payload = {
        intervene: !isSessionOngoing,
        session_start: !isSessionOngoing,
        phone_number: currentPhone,
        bot_number: userDetails?.bot_number,
      };

      const res = await actions.startEndSession(payload);

      if (res?.data?.status === false) {
        showSnackBar(res?.data?.data?.general_error || 'Something went wrong', 'error');
        return;
      }

      if (res?.status === 200) {
        showSnackBar(
          res?.data?.data ||
          (isSessionOngoing ? 'Session Ended Successfully.' : 'Session Started Successfully.'),
          'success'
        );

        // ✅ 1. Immediately update userDetails in-place — no navigation, no flash
        setUserDetails(prev => ({
          ...prev,
          intervene: !isSessionOngoing,
          session_start: !isSessionOngoing,
          intervened_by: !isSessionOngoing ? loggedInUser?.username : '',
        }));

        const updatedFilters = {
          ...interveneFilters,
          userType: 'intervene',
          page: 1,
          search: '',
        };

        // ✅ 2. Update filters & message list state instantly (no await)
        setInterveneFilters(updatedFilters);

        setMessageList((prev) => ({
          ...prev,
          filters: {
            ...prev.filters,
            page: 1,
            phone_number: currentPhone,
            intervened_by: !isSessionOngoing ? loggedInUser?.username : '',
          },
        }));

        // ✅ 3. Fire background refreshes — no await, no blocking
        const botNumber =
          interveneFilters?.botNumber ||
          interveneFilters?.channelObj?.wa_phone_number ||
          interveneFilters?.channelObj?.channel_number;

        if (botNumber) {
          actions.refreshChatCounts(botNumber).then((countRes) => {
            const totals = countRes?.data?.user_counts ?? {};
            setChatCounts({
              intervene: Number(totals?.intervene) || 0,
              min_15: Number(totals?.min_15) || 0,
              hrs_24: Number(totals?.hrs_24) || 0,
              history: Number(totals?.history) || 0,
              total: Number(totals?.total) || 0,
            });
          }).catch(() => {/* silent */ });
        }

        // ✅ 4. Refresh user list in background — no await, no page freeze
        actions.getUserListV1({ ...updatedFilters, resetUserList: true }).catch(() => { });

        // ✅ 5. Navigate without waiting for any of the above
        navigate(`/r/intervene-dashboard/intervene`);
      }
    } catch (error) {
      showSnackBar('Failed to start session', 'error');
    }
    finally {
      setIsInterveneLoading(false);
    }
  };

  const checkLastMessageDateTime = () => {
    if (!userDetails?.last_active_date) return true;
    const givenDate = new Date(indianTimeZone(userDetails.last_active_date));
    const currentDate = new Date();
    const timeDifference = currentDate.getTime() - givenDate.getTime();
    const twentyFourHoursInMilliseconds = 24 * 60 * 60 * 1000;
    return timeDifference > twentyFourHoursInMilliseconds;
  };
  const resetMessageList = useResetRecoilState(messageListAtom);

  // useEffect(() => {
  //   scrollToBottom();
  // }, [messageList]);

  useEffect(() => {
    // setPageNumber(1);
    // resetMessageList();
    // getMessagesByNumber({ ...userDetails, page: 1 });

    const handleBeforeUnload = () => {
      console.log('Window closed or refreshed.');
      // Optional: perform clean-up
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [userDetails]);

  useEffect(() => {
    if (!userDetails || Object.keys(userDetails).length === 0) {
      setShowMobileView({ userList: true, messageBox: false, userProfile: false });
    }
  }, [userDetails, setShowMobileView]);

  useEffect(() => {
    setHasFetchedMessages(false);
  }, [userDetails?.phone_number]);

  useEffect(() => {
    getMessagesByNumber({ ...messageList?.filters });
  }, [
    messageList?.filters?.phone_number,
    messageList?.filters?.page,
    messageList?.filters?.bot_number,
  ])

  const CurrentUsersModal = () => (
    <ModalForms
      show={showUsersModal}
      handleClose={() => setShowUsersModal(false)}
      modalHeader={`Active Users on Chat (${currentUsersOnChat.length})`}
      modalId="active-users-modal"
      size="md"
      modalBody={
        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
          {currentUsersOnChat.length > 0 ? (
            currentUsersOnChat.map((username, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  mb: 1,
                  backgroundColor: username === loggedInUser?.username ? '#E3F2FD' : '#F5F5F5',
                  borderRadius: '8px',
                  border: username === loggedInUser?.username ? '1px solid #2196F3' : '1px solid #E0E0E0',
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '16px',
                  }}
                >
                  {username?.charAt(0)?.toUpperCase()}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {username}
                  </Typography>
                  {username === loggedInUser?.username && (
                    <Typography variant="caption" sx={{ color: '#2196F3' }}>
                      You
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#4CAF50',
                  }}
                />
              </Box>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary">No active users</Typography>
            </Box>
          )}
        </Box>
      }
    />
  );

  const currentPhone = userDetails?.phone_number ?? '';
  const isInitialMessagesLoading = isLoading && !hasFetchedMessages && (messageList?.filters?.page ?? 1) === 1;

  if (prevPhoneRef.current !== currentPhone) {
    if (prevPhoneRef.current) {
      draftsRef.current[prevPhoneRef.current] = sendMessage;
    }
    prevPhoneRef.current = currentPhone;
    const restored = draftsRef.current[currentPhone] ?? '';
    if (sendMessage !== restored) {
      setSendMessage(restored);
    }
  }

  return userDetails && Object.keys(userDetails).length > 0 ? (
    <Box
      className="massage-nodes"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: '1px solid #C9D2FF',
        position: 'relative',
        borderRadius: '8px',
        backgroundColor: '#F5F7F9',
        // height: { sm: 'fit-content', md: 'fit-content', lg: '85vh', xl: '73vh' },
        height: { sm: 'fit-content', md: 'fit-content', lg: '100%', xl: '100%' },
      }}
    >
      <>
        <Box
          display="flex"
          alignItems="center"
          p={1}
          sx={{
            backgroundColor: 'white',
            borderRadius: '8px 8px 0 0',
            justifyContent: { xs: 'center', sm: 'space-between' },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: 'pointer', mr: { xs: 1, sm: 2 } }}
          // onClick={() =>
          //   setShowMobileView({ userList: false, messageBox: false, userProfile: true })
          // }
          >
            {/* <img
              src={Avatar}
              alt="avatar"
              style={{
                width: 45,
                height: 45,
                borderRadius: '50%',
                border: '2px solid #e0e0e0'
              }}
            /> */}
            <Box
              display={{ xs: 'flex', sm: 'none' }}
              paddingRight={{ xs: '5px', sm: 0 }}
              alignItems="center"
            >
              {' '}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMobileView({ userList: true, messageBox: false, userProfile: false });
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Box>
            <MuiAvatar
              alt={userDetails?.whatsapp_name || userDetails?.phone_number}
              src={userDetails?.whatsapp_name ? undefined : Avatar} // Show image only if no valid name for letter
              onClick={() =>
                setShowMobileView({ userList: false, messageBox: false, userProfile: true })
              }
              sx={{
                width: { xs: 35, sm: 45 },
                height: { xs: 35, sm: 45 },
                borderRadius: '50%',
                border: '2px solid #e0e0e0',

                // Optionally, add a background color if displaying a letter.
                // MUI might pick one, or you can set it explicitly.
                // bgcolor: userDetails?.whatsapp_name ? 'common.white' : undefined,
                // color: userDetails?.whatsapp_name ? 'common.black' : undefined,
                bgcolor: userDetails?.whatsapp_name ? 'primary.main' : undefined,
                color: userDetails?.whatsapp_name ? 'primary.contrastText' : undefined,
              }}
            >
              {userDetails?.whatsapp_name
                ? userDetails?.whatsapp_name?.trim()[0].toUpperCase()
                : null}
            </MuiAvatar>
            <Box sx={{ ml: { xs: 1, sm: 2 } }}>
              <Typography
                variant="h6"
                className="user-name-top-head"
                sx={{
                  fontSize: { xs: '13px', sm: '16px' },
                  fontWeight: 500,
                  color: '#000',
                }}
              >
                {userDetails?.whatsapp_name
                  ? `${userDetails?.whatsapp_name} (${userDetails?.phone_number})`
                  : userDetails?.phone_number}
              </Typography>
              {isMobile && userDetails?.last_active_date && (
                <Typography
                  variant="body2"
                  sx={{ color: '#404040', fontSize: '12px', fontWeight: 400 }}
                >
                  Last Seen{' '}
                  {dateFormate(indianTimeZone(userDetails?.last_active_date), 'DD/MM/YYYY hh:mm A')}
                </Typography>
              )}
            </Box>
            {/* chip for live call indicator */}
            {/* {userDetails?.intervene && userDetails?.session_start && userDetails?.intervened_by ? (
              // {interveneFilters?.userType === 'intervene' && userDetails?.intervened_by ? (
              <Box
                marginLeft={5}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: '8px',
                  background: '#F6F8FF',
                  border: '1px solid #1631B1',
                  borderRadius: '10px',
                  padding: '7px 13px',
                  width: 'fit-content',
                }}
              >
                <SupportAgentIcon sx={{ color: '#1631B1', fontSize: '20px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: '14px', color: 'primary.main' }}>
                    {userDetails?.intervened_by}
                  </Typography>
                </Box>
              </Box>
            ) : null} */}
          </Box>
          <Box sx={{ display: 'flex', gap: { xs: '0px', sm: '8px' }, alignItems: 'center' }}>
            <Tooltip
              title={
                currentUsersOnChat.length > 0
                  ? `Active Users: ${currentUsersOnChat.join(', ')}`
                  : 'Refresh Messages'
              }
              placement="bottom"
            >
              <IconButton
                onClick={() => {
                  if (currentUsersOnChat.length > 0) {
                    setShowUsersModal(true);
                  } else {
                    // getMessagesByNumber({ ...userDetails, page: 1 });
                    getMessagesByNumber({ ...messageList?.filters, page: 1 });
                  }
                }}
                sx={{
                  m: { xs: '4px', sm: 0 },
                  backgroundColor: currentUsersOnChat.length > 0 ? '#1631B1' : '#808080',
                  color: '#fff',
                  width: '22px',
                  height: '22px',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: currentUsersOnChat.length > 0 ? '#0d1f6b' : '#bdbdbd',
                    color: currentUsersOnChat.length > 0 ? '#fff' : '#000',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress sx={{ fontSize: '20px' }} />
                ) : currentUsersOnChat.length > 0 ? (
                  <>
                    <PeopleIcon sx={{ fontSize: '20px' }} />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                      }}
                    >
                      {currentUsersOnChat.length}
                    </Box>
                  </>
                ) : (
                  <RefreshIcon sx={{ fontSize: '20px' }} />
                )}
              </IconButton>
            </Tooltip>
            {/* <IconButton
              onClick={() => getMessagesByNumber({ ...messageList?.filters, page: 1 })}
              // onClick={() => getMessagesByNumber({ ...userDetails, page: 1 })}
              sx={{
                m: { xs: "4px", sm: 0 },
                backgroundColor: '#808080',
                color: '#fff',
                width: '22px',
                height: '22px',
                '&:hover': {
                  backgroundColor: '#bdbdbd',
                  color: '#000',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress sx={{ fontSize: '20px' }} />
              ) : (
                <RefreshIcon sx={{ fontSize: '20px' }} />
              )}
            </IconButton> */}
            {userDetails?.payload?.gmapLink && (
              <IconButton onClick={() => window.open(userDetails.payload.gmapLink, '_blank')}>
                <LocationOnIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            )}
            <IconButton
              onClick={() => setModalShowCall(true)}
              sx={{ p: { xs: '4px', sm: 'auto' } }}
            >
              <PhoneIcon sx={{ fontSize: '20px' }} />
            </IconButton>
            <IconButton
              onClick={(e) => setFormAnchorEl(e.currentTarget)}
              sx={{ p: { xs: '4px', sm: 'auto' } }}
            >
              <InfoRoundedIcon sx={{ color: '#1631B1', fontSize: '22px' }} />
            </IconButton>
            <Menu anchorEl={formAnchorEl} open={formMenuOpen} onClose={() => setFormAnchorEl(null)}>
              <MenuItem onClick={() => navigate(`/r/lead-web-form/${userDetails.phone_number}`)}>
                B2B2C Webform
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* <Box
        ref={messageBoxRef}
        onScroll={handleScroll}
        sx={{
          height: '60vh',
          overflowY: 'auto',
          backgroundColor: '#f9f9f9',
          px: 2,
         borderRadius: '8px',
          border: '1px solid #e0e0e0',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
        }}
      >
        {messageList?.messages?.map((msg, idx) => (
          <MessageNode key={idx} message={msg} />
        ))}
      </Box> */}
        <Box sx={{ borderRadius: '8px' }}>
          {isInitialMessagesLoading ? (
            <Box
              sx={{
                height: { xs: '63vh', sm: '79vh', md: '68vh', lg: '64vh', xl: '72vh' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : messageList?.messages?.length > 0 ? (
            <Box
              ref={messageBoxRef}
              onScroll={handleScroll}
              className="multi-msg-box"
              sx={{
                height: { xs: '63vh', sm: '79vh', md: '68vh', lg: '64vh', xl: '72vh' },
                // height: { xs: '56vh', sm: '56vh', md: '56vh', lg: '62vh', xl: '72vh' },
                overflowY: 'auto',
                // backgroundColor: 'white',
                backgroundColor: 'transparent',
                px: 2,
                pt: 1,
                borderRadius: '8px',
                WebkitOverflowScrolling: 'touch',
                // border: '1px solid #e0e0e0',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0,0,0,0.3) transparent',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              {/* {messageList.messages.map((msg, idx) => (
                <MessageDesign key={idx} message={msg}>
                  <MessageNode message={msg} />
                </MessageDesign>
              ))} */}
              {messageList?.messages?.map((msg, idx) => {
                const prevMsg = messageList?.messages[idx - 1];
                const currDate = formatDateLabel(msg.created_at);
                const prevDate = prevMsg ? formatDateLabel(prevMsg?.created_at) : null;

                const showDateLabel = currDate !== prevDate;

                return (
                  <React.Fragment key={idx}>
                    {showDateLabel && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          my: 1.5,
                          gap: 0.8,
                        }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            backgroundColor: '#e0e0e0',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#333',
                          }}
                        >
                          {currDate}
                        </Box>
                        {/* <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            backgroundColor: '#e0e0e0',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#333',
                          }}
                        >
                         {checkSession ? 'Sharda admin End Intervene' : 'Sharda admin Start Intervene'}
                        </Box> */}
                      </Box>
                    )}
                    <MessageDesign key={idx} message={msg}>
                      <MessageNode message={msg} />
                    </MessageDesign>
                    {/* <MessageDesign message={msg}>
                      <MessageNode message={msg} />
                    </MessageDesign> */}
                  </React.Fragment>
                );
              })}
            </Box>
          ) : hasFetchedMessages ? (
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: { xs: 2, md: 3 },
                bgcolor: '#f0f2f5',
                backgroundImage: `
                  radial-gradient(circle at 20px 20px, rgba(0,0,0,0.02) 1px, transparent 1px),
                  radial-gradient(circle at 80px 80px, rgba(0,0,0,0.02) 1px, transparent 1px)
                `,
                backgroundSize: '100px 100px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                mb: '170px',
              }}
            >
              <Box sx={{ textAlign: 'center', color: '#9e9e9e' }}>
                <SmartToyIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>
                  No messages yet
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                height: { xs: '63vh', sm: '79vh', md: '68vh', lg: '64vh', xl: '72vh' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
          <Box>
            {selectedFiles.length > 0 && (
              <FileUploadPreview selectedFiles={selectedFiles} deleteFile={deleteFile} />
            )}

            <Paper
              component="form"
              sx={{
                margin: '15px',
                marginTop: '0px',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f5f7f9',
                border: '1px solid #E4E4E4',
                boxShadow: 'none',
              }}
            >
              <Box
                sx={{
                  '& button': {
                    padding: '0px',
                  },
                }}
              >
                {canEndIntervene && !sendMessage.trim().length && !selectedFiles?.length ? (
                  <IconButton
                    type="button"
                    aria-label="voice"
                    sx={{
                      '& .audio-recorder': {
                        background: 'transparent',
                        boxShadow: 'none',
                        padding: '0px',
                      },
                    }}
                  >
                    <VoiceRecorder />
                  </IconButton>
                ) : null}
              </Box>
              {canEndIntervene && (
                <IconButton
                  sx={{ p: '10px' }}
                  aria-label="menu"
                  size="large"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <AttachFileIcon fontSize="inherit" sx={{ fontSize: '18px' }} />
                </IconButton>
              )}
              <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {!checkLastMessageDateTime() && (
                  <>
                    <MenuItem
                      onClick={() => uploadRefs.current.image?.click()}
                      disabled={!checkSession}
                    >
                      <ListItemIcon>
                        <ImageIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Image"
                        secondary="JPEG, PNG • Max 5 MB"
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: 'text.secondary' },
                        }}
                      />
                      <input
                        ref={(el) => (uploadRefs.current.image = el)}
                        hidden
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleFileChange}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => uploadRefs.current.doc?.click()}
                      disabled={!checkSession}
                    >
                      <ListItemIcon>
                        <DescriptionIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Document"
                        secondary="PDF, DOC, XLS, PPT, TXT, CSV • Max 100 MB"
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: 'text.secondary' },
                        }}
                      />
                      <input
                        ref={(el) => (uploadRefs.current.doc = el)}
                        hidden
                        type="file"
                        accept=".pdf,application/pdf,.doc,application/msword,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xls,application/vnd.ms-excel,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.ppt,application/vnd.ms-powerpoint,.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation,.txt,text/plain,.csv,text/csv"
                        onChange={handleFileChange}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => uploadRefs.current.audio?.click()}
                      disabled={!checkSession}
                    >
                      <ListItemIcon>
                        <AudiotrackIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Audio"
                        secondary="AAC, MP4, MP3, AMR, OGG • Max 16 MB"
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: 'text.secondary' },
                        }}
                      />
                      <input
                        ref={(el) => (uploadRefs.current.audio = el)}
                        hidden
                        type="file"
                        accept="audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg"
                        onChange={handleFileChange}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => uploadRefs.current.video?.click()}
                      disabled={!checkSession}
                    >
                      <ListItemIcon>
                        <OndemandVideoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Video"
                        secondary="MP4 • Max 16 MB"
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: 'text.secondary' },
                        }}
                      />
                      <input
                        ref={(el) => (uploadRefs.current.video = el)}
                        hidden
                        type="file"
                        accept="video/mp4,video"
                        onChange={handleFileChange}
                      />
                    </MenuItem>
                  </>
                )}
                <MenuItem
                  onClick={() => {
                    setModalShowTemp(true);
                    resetWhatsappTempFormData();
                  }}
                >
                  <ListItemIcon>
                    <SendIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Send Template" />
                </MenuItem>
              </Menu>
              {checkLastMessageDateTime() ? (
                // Case 1: 24+ hours have passed — just show the error text
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ pl: 2, pt: 0.5, fontSize: 16, flex: 1 }}
                >
                  This Session has expired
                </Typography>
              ) : !checkSession ? (
                // Case 2: Not in session — show message + button
                <>
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ pl: 1, pt: 0.5, fontSize: 16, flex: 1 }}
                  >
                    Please click 'Start Intervene' to chat.
                  </Typography>
                  {interveneFilters?.userType !== 'history' && (
                    <Box sx={{ pr: 1 }}>
                      <LoadingButton
                        buttonName="Start Intervene"
                        variant="contained"
                        size="small"
                        onClickButton={handleClickStartSession}
                        isLoading={isInterveneLoading}
                        sx={{
                          mx: 1,
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          borderColor: '#061766',
                          background: '#2E419A',
                          color: '#fff',
                          minWidth: '140px',
                          '&:hover': {
                            background: '#243582',
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              ) : (
                // Case 3: In active session — show input box and full controls
                <>
                  <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                  {canEndIntervene ? (
                    <InputBase
                      sx={{ ml: 1, flex: 1 }}
                      fullWidth
                      multiline
                      maxRows={6}
                      placeholder="Write text..."
                      inputProps={{ 'aria-label': 'Write text...' }}
                      value={sendMessage}
                      onChange={(e) => {
                        const val = e.target.value;
                        draftsRef.current[userDetails?.phone_number ?? ''] = val;
                        setSendMessage(val);
                      }}
                      // onChange={(e) => setSendMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (sendMessage.trim().length) sendMessageByNumber();
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, flex: 1 }}>
                      <SupportAgentIcon
                        sx={{ fontSize: '18px', color: 'primary.main', flexShrink: 0 }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '13px', color: '#555' }}>
                        This user is already in a chat with{' '}
                        <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {userDetails?.intervened_by || 'another agent'}
                        </Box>
                      </Typography>
                    </Box>
                    // <Typography
                    //   variant="caption"
                    //   color="error"
                    //   sx={{ pl: 2, pt: 0.5, fontSize: 16, flex: 1 }}
                    // >
                    //   You can’t send messages right now because another chat is currently active
                    // </Typography>
                  )}
                  <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                  {sendMessage.trim().length || selectedFiles?.length ? (
                    <IconButton
                      sx={{ p: '10px' }}
                      onClick={sendMessageByNumber}
                      disabled={isMessageSending}
                    >
                      <SendIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  ) : (
                    ''
                  )}
                  {canEndIntervene && interveneFilters?.userType !== 'history' && (
                    <LoadingButton
                      buttonName="End Intervene"
                      variant="contained"
                      size="small"
                      onClickButton={handleClickStartSession}
                      isLoading={isInterveneLoading}
                      sx={{
                        mx: 1,
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        borderColor: '#061766',
                        background: '#2E419A',
                        color: '#fff',
                        minWidth: '130px',
                        '&:hover': {
                          background: '#243582',
                        },
                      }}
                    />
                  )}
                </>
              )}
            </Paper>
          </Box>
        </Box>
        {modalShowCall && (
          <ModalForms
            show={modalShowCall}
            handleClose={() => setModalShowCall(false)}
            modalBody={<CallingModal userDetails={userDetails} />}
            modalHeader="Quick Call"
            modalId="calling"
            size="sm"
          />
        )}

        {modalShowTemp && (
          <ModalForms
            show={modalShowTemp}
            handleClose={() => setModalShowTemp(false)}
            modalBody={<SendTemplateModal handleCloseModal={() => setModalShowTemp(false)} />}
            modalHeader="Send Templates"
            modalId="template_modal"
            size="md"
          />
        )}
      </>
      <CurrentUsersModal />
    </Box>
  ) : isLoading ? (
    <LoadingWrapper />
  ) : (
    <Box
      sx={{
        height: '100%',
        backgroundColor: 'transparent',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box sx={{ textAlign: 'center', px: 3 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: '#EEEDFE',
            border: '1px solid #AFA9EC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <PeopleIcon sx={{ fontSize: 32, color: '#534AB7' }} />
        </Box>
        <Typography variant="h6" fontWeight={500} sx={{ color: '#1a1a2e', mb: 0.5 }}>
          Select a user to view chat
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', maxWidth: 260, mx: 'auto' }}>
          Choose a contact from the list to start messaging
        </Typography>
      </Box>
    </Box>
  );
  // );
};

export default MessageBox;
